import React, { useMemo } from 'react';
import { Row, Col, Radio, Select } from 'antd';
import PropTypes from 'prop-types';
import { Label } from '@4beefopag/4bee-fopag-core/dist/atoms';
import { MODULE_PAYROLL_ORDER } from '../../../constants/modules';
import { APONTAMENTO } from '../../../services/apontamentoProducao';
import SelectFolha from '../../atoms/SelectFolha';
import ApontamentoCompetenciaSelector from './ApontamentoCompetenciaSelector';
import ApontamentoRequisicaoSelector from './ApontamentoRequisicaoSelector';
import ApontamentoFilterBar from './ApontamentoFilterBar';
import MultiSelect from '../../molecules/MultiSelect';

const { Option } = Select;

/**
 * Componente Organism para filtros do apontamento
 */
function ApontamentoFilters({
  payrollSheet,
  competence,
  competences,
  type,
  payroll,
  payrolls,
  loadingPayrolls,
  parameterization,
  filterName,
  selectedCollaborators,
  areAllSelectedCollaborators,
  display,
  onPayrollSheetChange,
  onCompetenceChange,
  onTypeChange,
  onPayrollChange,
  onNewPayrollClick,
  onFilterNameChange,
  onCollaboratorsChange,
  onCollaboratorsSelectAll,
  onGoToFirstPage,
}) {
  const sortedCooperators = useMemo(
    () =>
      (payroll?.items ?? [])
        .map(({ cooperator }) => cooperator)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR', { sensitivity: 'base' })),
    [payroll?.items]
  );

  return (
    <Row
      type="flex"
      gutter={[16, 16]}
    >
      <Col
        xs={24}
        sm={24}
        md={12}
        lg={7}
        xl={12}
      >
        <SelectFolha
          module={MODULE_PAYROLL_ORDER}
          idFolhaSelecionada={payrollSheet?.id}
          onChange={onPayrollSheetChange}
        />
      </Col>

      <ApontamentoCompetenciaSelector
        competence={competence}
        competences={competences}
        disabled={!payrollSheet || !display.habilitarFiltros}
        parameterization={parameterization}
        onCompetenceChange={onCompetenceChange}
      />

      <Col
        xs={24}
        sm={24}
        md={12}
        lg={8}
        xl={8}
      >
        <div style={{ marginTop: '28px' }}>
          <Radio.Group
            buttonStyle="solid"
            disabled={!competence || !display.habilitarFiltros}
            value={type}
          >
            <Radio.Button
              value={APONTAMENTO.tiposRequisicoes.adiantamento}
              onClick={() => onTypeChange(APONTAMENTO.tiposRequisicoes.adiantamento)}
            >
              Adiantamento
            </Radio.Button>
            <Radio.Button
              value={APONTAMENTO.tiposRequisicoes.mensal}
              onClick={() => onTypeChange(APONTAMENTO.tiposRequisicoes.mensal)}
            >
              Mensal
            </Radio.Button>
          </Radio.Group>
        </div>
      </Col>

      <ApontamentoRequisicaoSelector
        requisicao={payroll}
        requisicoes={payrolls}
        loading={loadingPayrolls}
        disabled={loadingPayrolls || !display.habilitarFiltros}
        parametrizacao={parameterization}
        tipo={type}
        onRequisicaoChange={onPayrollChange}
        onNovaRequisicaoClick={onNewPayrollClick}
      />

      <ApontamentoFilterBar
        filterName={filterName}
        onFilterNameChange={onFilterNameChange}
        disabled={!(display.habilitarFiltros && payroll)}
      />

      <Col
        xs={24}
        sm={24}
        md={24}
        lg={12}
        xl={12}
      >
        <Label label="Cooperados">
          <MultiSelect
            loading={loadingPayrolls}
            disabled={!(payroll && payroll.items)}
            maxTagTextLength={15}
            maxTagCount={2}
            requisicao={payroll}
            value={selectedCollaborators}
            areAllSelected={areAllSelectedCollaborators}
            // Espaço não separa tokens, permitindo buscar por nome completo
            tokenSeparators={[',']}
            // Mantém o termo buscado após selecionar, para seleção múltipla na lista filtrada
            autoClearSearchValue={false}
            ignoreAccents
            onChangeAllSelected={onCollaboratorsSelectAll}
            onChange={(values) => {
              onCollaboratorsChange(values);
              onGoToFirstPage();
            }}
          >
            {sortedCooperators.map((cooperator) => (
              <Option
                key={`colab-${cooperator.id}`}
                value={cooperator.id}
              >
                {cooperator.name}
              </Option>
            ))}
          </MultiSelect>
        </Label>
      </Col>
    </Row>
  );
}

ApontamentoFilters.propTypes = {
  payrollSheet: PropTypes.shape({
    id: PropTypes.number,
  }),
  competence: PropTypes.string,
  competences: PropTypes.arrayOf(
    PropTypes.shape({
      comp: PropTypes.string,
    })
  ).isRequired,
  type: PropTypes.string.isRequired,
  payroll: PropTypes.shape({
    id: PropTypes.number,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        cooperator: PropTypes.shape({
          id: PropTypes.number,
          nome: PropTypes.string,
        }),
      })
    ),
  }),
  payrolls: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.number,
      descricao: PropTypes.string,
    })
  ),
  loadingPayrolls: PropTypes.bool,
  parameterization: PropTypes.shape({
    id: PropTypes.number,
    nome: PropTypes.string,
    email_alerta: PropTypes.string,
    exibe_param_apontamento: PropTypes.bool,
    atual_comp_ap_qtd_dias: PropTypes.number,
  }),
  filterName: PropTypes.string,
  selectedCollaborators: PropTypes.arrayOf(PropTypes.number).isRequired,
  areAllSelectedCollaborators: PropTypes.bool.isRequired,
  display: PropTypes.shape({
    habilitarFiltros: PropTypes.bool,
  }).isRequired,
  onPayrollSheetChange: PropTypes.func.isRequired,
  onCompetenceChange: PropTypes.func.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  onPayrollChange: PropTypes.func.isRequired,
  onNewPayrollClick: PropTypes.func.isRequired,
  onFilterNameChange: PropTypes.func.isRequired,
  onCollaboratorsChange: PropTypes.func.isRequired,
  onCollaboratorsSelectAll: PropTypes.func.isRequired,
  onGoToFirstPage: PropTypes.func.isRequired,
};

ApontamentoFilters.defaultProps = {
  payrollSheet: undefined,
  competence: undefined,
  payroll: undefined,
  payrolls: [],
  loadingPayrolls: false,
  parameterization: undefined,
  filterName: undefined,
};

export default ApontamentoFilters;
